/**
 * The record behind a policy: who approved what, and every version.
 *
 * This is the page a surveyor's second question lands on ("who approved it,
 * and when?") and the page a lawyer's only question lands on ("what was in
 * force on that date?"). Neither is answered by a status badge. The approval
 * history lists each time the document went for sign-off and every decision
 * along the way, with the reason when it came back; the version list opens
 * any earlier version as it was.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { call } from '../../lib/callable';
import { formatDate } from './policyTable';
import { readableAttachment, type PolicyDetail } from './usePolicy';
import { usePolicyVersions, versionStatusLabel } from './useVersions';
import { useVisible } from '../org/useVisibility';
import { usePolicyReviews, reviewOutcome } from './useReviewHistory';
import { ApprovalRecord, CANCELLATION_CAUSE } from './ApprovalRecord';
import styles from '../../shared/Page.module.css';
import own from './PolicyHistory.module.css';

export interface HistoryInstance {
  instanceId: string;
  status: string;
  artifactVersionNumber?: number | null;
  changeSummary?: string | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
  startedAt?: unknown;
  completedAt?: unknown;
  /**
   * The callable has always sent these. They were declared away here, which is
   * why the page could say a document was "Withdrawn" when nothing of the sort
   * had happened: the two fields that tell a real withdrawal from a system
   * cancellation were dropped before the component ever saw them.
   */
  withdrawal?: { byName?: string | null; reason?: string } | null;
  cancellation?: { cause?: string } | null;
}

export interface HistoryDecision {
  decisionId: string;
  instanceId: string;
  stepOrder: number;
  stepName?: string;
  userName?: string;
  userEmail?: string;
  decisionType: 'approved' | 'rejected' | 'requestChanges';
  comments?: string;
  decidedAt?: unknown;
  /** What an organized body resolved, and when it met. Recorded for every outcome, not only approval. */
  committee?: { meetingDate: string; vote?: string | null; minutesRef?: string | null } | null;
}

export interface ApprovalHistory {
  instances: HistoryInstance[];
  decisions: HistoryDecision[];
  /**
   * What the server left out because the organization does not show it to this
   * person. Absent on an older response, which is the same as nothing
   * withheld.
   */
  withheld?: { comments?: boolean; committeeEvidence?: boolean };
}

/**
 * A day, from whatever shape a timestamp arrives in over a callable: an ISO
 * string, a Firestore Timestamp serialized as `{_seconds}` or `{seconds}`,
 * or an object with toDate().
 */
export function toDay(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (typeof value === 'object') {
    const v = value as { _seconds?: number; seconds?: number; toDate?: () => Date };
    const secs = typeof v._seconds === 'number' ? v._seconds : typeof v.seconds === 'number' ? v.seconds : null;
    if (secs !== null) return new Date(secs * 1000).toISOString().slice(0, 10);
    if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  }
  return null;
}

/**
 * The full moment, not the day. Two decisions taken an hour apart used to be
 * indistinguishable on screen although the server stores both to the second,
 * which left a reader unable to put a chain in order.
 */
export function toMoment(value: unknown): string | null {
  const d = toDate(value);
  return d ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'string') { const d = new Date(value); return Number.isNaN(d.getTime()) ? null : d; }
  if (typeof value === 'object') {
    const v = value as { _seconds?: number; seconds?: number; toDate?: () => Date };
    const secs = typeof v._seconds === 'number' ? v._seconds : typeof v.seconds === 'number' ? v.seconds : null;
    if (secs !== null) return new Date(secs * 1000);
    if (typeof v.toDate === 'function') return v.toDate();
  }
  return null;
}

export const DECISION_LABEL: Record<HistoryDecision['decisionType'], string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  requestChanges: 'Changes requested',
};

/**
 * CANCELLED IS NOT WITHDRAWN. A person pulling a document back is one thing;
 * the system closing the round because the author resubmitted, an approver
 * published directly, a submission rolled back or the document was retired is
 * another. All five land on `status: 'cancelled'`, and this used to call every
 * one of them "Withdrawn" — a record asserting a deliberate act nobody took.
 * A real withdrawal carries `withdrawal`. Anything else says so, and says why
 * when the cause was recorded, or says plainly that it was not.
 */
export function instanceLabel(inst: Pick<HistoryInstance, 'status' | 'withdrawal' | 'cancellation'>): { label: string; tone: 'ok' | 'info' | 'neutral' | 'danger' } {
  switch (inst.status) {
    case 'approved': return { label: 'Approved', tone: 'ok' };
    case 'rejected': return { label: 'Returned', tone: 'danger' };
    case 'cancelled': return inst.withdrawal ? { label: 'Withdrawn', tone: 'neutral' } : { label: 'Cancelled', tone: 'neutral' };
    default: return { label: 'In progress', tone: 'info' };
  }
}

export function useApprovalHistory(policyId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['approval-history', policyId],
    enabled: Boolean(policyId) && enabled,
    queryFn: () => call<{ artifactId: string }, ApprovalHistory>('getApprovalHistoryFn', { artifactId: policyId! }),
  });
}

export function ApprovalHistorySection({ policyId }: { policyId: string }) {
  const { data, isLoading, error } = useApprovalHistory(policyId, true);
  const [open, setOpen] = useState<string | null>(null);
  const instances = data?.instances ?? [];

  return (
    <section className={styles.section} aria-labelledby="approval-history-title">
      <h2 className={styles.sectionTitle} id="approval-history-title">Approval history</h2>
      {isLoading && <p className={own.muted}>Loading approvals…</p>}
      {error && <p className={own.muted}>The approval history could not be loaded.</p>}
      {!isLoading && !error && instances.length === 0 && (
        <p className={own.muted}>Not yet submitted for approval.</p>
      )}
      {/*
        * SAY IT IS WITHHELD RATHER THAN LEAVE A BLANK. A reader looking at a
        * round that was returned to its author sees that it was returned and
        * not why, and a missing reason with no explanation reads as a product
        * that lost it. The server decides; this only reports.
        */}
      {data?.withheld?.comments && instances.length > 0 && (
        <p className={own.muted}>
          Your organization does not show the comments written during approval to your role.
          What was decided, by whom and when is below.
        </p>
      )}
      <ol className={own.rounds}>
        {instances.map((inst) => {
          const tone = instanceLabel(inst);
          const decisions = (data?.decisions ?? []).filter((d) => d.instanceId === inst.instanceId);
          return (
            <li key={inst.instanceId} className={own.round}>
              <div className={own.roundHead}>
                <span className={`${styles.tag} ${styles[tone.tone]}`}>{tone.label}</span>
                <span className={own.roundMeta}>
                  {inst.artifactVersionNumber != null && `Version ${inst.artifactVersionNumber} · `}
                  submitted {formatDate(toDay(inst.startedAt))}
                  {(inst.submittedByName || inst.submittedByEmail) && ` by ${inst.submittedByName ?? inst.submittedByEmail}`}
                </span>
              </div>
              {inst.status === 'cancelled' && !inst.withdrawal && (
                <p className={own.muted}>
                  {inst.cancellation?.cause
                    ? CANCELLATION_CAUSE[inst.cancellation.cause] ?? 'Closed by StandingDocs.'
                    : 'Reason not recorded. Nobody withdrew it.'}
                </p>
              )}
              {inst.changeSummary && <p className={own.summary}>{inst.changeSummary}</p>}
              {decisions.length > 0 && (
                <ul className={own.decisions}>
                  {decisions.map((d) => (
                    <li key={d.decisionId}>
                      <span className={own.decisionWhat}>{DECISION_LABEL[d.decisionType]}</span>
                      {' '}at {d.stepName ?? `step ${d.stepOrder}`} by {d.userName ?? d.userEmail ?? 'an approver'}
                      {' '}on <span className="tnum">{formatDate(toDay(d.decidedAt))}</span>
                      {d.comments && <div className={own.comment}>“{d.comments}”</div>}
                    </li>
                  ))}
                </ul>
              )}
              <div className={styles.actions}>
                <button type="button" className={styles.btn} aria-expanded={open === inst.instanceId}
                  onClick={() => setOpen(open === inst.instanceId ? null : inst.instanceId)}>
                  {open === inst.instanceId ? 'Hide the approval record' : 'View approval record'}
                </button>
              </div>
              {open === inst.instanceId && <ApprovalRecord instanceId={inst.instanceId} />}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function VersionHistorySection({ policy, orgId, admin = false }: { policy: PolicyDetail; orgId: string | null; admin?: boolean }) {
  const visible = useVisible();
  const { data, isLoading, error } = usePolicyVersions(orgId, policy.id, visible.versionHistory);
  const versions = data ?? [];

  /**
   * An organization that does not show earlier versions to this person gets no
   * section at all, rather than an empty one that invites the question.
   * Firestore refuses the documents and getPolicyFileUrl refuses their files,
   * so this is presentation over a decision already made elsewhere.
   */
  if (!visible.versionHistory) return null;

  return (
    <section className={styles.section} aria-labelledby="version-history-title">
      <h2 className={styles.sectionTitle} id="version-history-title">Versions</h2>
      {isLoading && <p className={own.muted}>Loading versions…</p>}
      {error && <p className={own.muted}>The versions could not be loaded.</p>}
      {!isLoading && !error && versions.length === 0 && (
        <p className={own.muted}>Not yet published. The first published version will be listed here and kept on record.</p>
      )}
      {versions.length > 0 && (
        <div className={own.scroller}>
          <table className={own.table}>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Status</th>
                {/* Two dates, never one. A policy approved in March and
                    effective in April has both, and collapsing them into a
                    single column made the record say something untrue. */}
                <th scope="col">Published</th>
                <th scope="col">Effective</th>
                <th scope="col">Approved by</th>
                <th scope="col">What changed</th>
                <th scope="col"><span className="vh">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const readable = readableAttachment({ attachments: v.attachments, documentId: v.documentId });
                const label = versionStatusLabel(v, policy.currentVersionId);
                const approver = v.approvedByName ?? v.publishedByName;
                return (
                  <tr key={v.id}>
                    <td className="tnum">v{v.versionNumber}</td>
                    <td><span className={`${styles.tag} ${label === 'Current' ? styles.ok : styles.neutral}`}>{label}</span></td>
                    <td className="tnum">
                      {v.publishedAt
                        ? <span title={v.publishedAtMoment ?? undefined}>{formatDate(v.publishedAt)}</span>
                        : <span className={styles.absent}>&mdash;</span>}
                    </td>
                    <td className="tnum">{v.effectiveDate ? formatDate(v.effectiveDate) : <span className={styles.absent}>&mdash;</span>}</td>
                    {/* A record that never stored who approved it says so,
                        rather than borrowing a name from somewhere else. */}
                    <td>{approver ?? <span className={styles.absent}>Not recorded</span>}</td>
                    <td>
                      {v.changeSummary ?? <span className={styles.absent}>&mdash;</span>}
                      {v.changeNotes && <div className={own.note}>{v.changeNotes}</div>}
                    </td>
                    <td>
                      <div className={own.rowLinks}>
                        {readable && (
                          <Link to={`/policy/${policy.id}/view?version=${encodeURIComponent(v.id)}`} className={styles.btn}>
                            Open v{v.versionNumber}
                          </Link>
                        )}
                        {/* EVIDENCE FOR THIS VERSION, not only for the one in
                            force. The report has always accepted a version id
                            and nothing ever sent one, so a policy everybody had
                            acknowledged fell off the outstanding list and its
                            evidence became unreachable. */}
                        {admin && (
                          <Link to={`/admin/acknowledgements/${policy.id}?version=${encodeURIComponent(v.id)}`} className={styles.btn}>
                            Who acknowledged v{v.versionNumber}
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * PERIODIC REVIEWS: the evidence the product recorded and never showed.
 *
 * "Reviewed, no changes" has always written who reviewed, when, their notes
 * and the next date it set. Nothing read it, so a hospital asked to prove a
 * policy had been reviewed on its cycle could not produce the record its own
 * system had kept. Every review shows, not just the latest, and none of them
 * is a version: no document was made, so no version was.
 */
export function ReviewHistorySection({ policy, orgId }: { policy: PolicyDetail; orgId: string | null }) {
  const reviews = usePolicyReviews(orgId, policy.id);

  if (reviews.isLoading) return null;
  if (reviews.isError) {
    return (
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Periodic reviews</h2>
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          We couldn&rsquo;t load the review history.{' '}
          <button type="button" className={own.linkBtn} onClick={() => void reviews.refetch()}>Try again</button>
        </div>
      </section>
    );
  }

  const rows = reviews.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Periodic reviews</h2>
      <p className={styles.itemMeta}>
        A review with no changes does not make a new version, so these are kept apart from the versions above.
      </p>
      <ul className={styles.list}>
        {rows.map((r) => (
          <li key={r.id} className={styles.listItem}>
            <div>
              <div className={styles.itemName}>{reviewOutcome(r)}</div>
              <div className={styles.itemMeta}>
                {toMoment(r.reviewedAt)}
                {' by '}
                {r.reviewerName ?? r.reviewerEmail ?? <span className={styles.absent}>somebody the record does not name</span>}
                {r.nextReviewDate && ` · next review ${toDay(r.nextReviewDate)}`}
              </div>
              {r.notes && <p className={own.note}>{r.notes}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * HOW THIS DOCUMENT GOT HERE, WHEN IT ARRIVED BY MIGRATION.
 *
 * Read-only, and shown only for a policy that was imported. A customer
 * eventually asks "did we write this in StandingDocs, or did it come across
 * from the old system?", and until now the answer lived on an import job
 * record an administrator could delete, with the policy left holding a job id
 * pointing at nothing.
 *
 * The facts are on the POLICY (`importSource`), copied there once at import, so
 * deleting the job changes nothing here. Every line is optional: policies
 * imported before that existed show what is known and nothing else. Nothing on
 * this panel says published, approved or reviewed, because StandingDocs
 * observed none of those; it says the document was imported, from what, by
 * whom and when.
 */
export function ImportProvenanceSection({ policy }: { policy: PolicyDetail }) {
  const src = policy.importedFrom;
  if (!src) return null;

  const rows: Array<[string, React.ReactNode]> = [];
  const known = (label: string, value: React.ReactNode | null | undefined) => {
    if (value === null || value === undefined || value === '') return;
    rows.push([label, value]);
  };

  known('Imported', toMoment(src.importedAt));
  known('By', src.importedByName ?? src.importedBy);
  known('From the spreadsheet', src.sourceFileName);
  known('Line of that spreadsheet', src.sourceRowNumber);
  known('The old system’s id', src.legacyId);
  if (src.originalPolicyNumber && src.originalPolicyNumber !== policy.policyNumber) {
    known('Numbered in the old system', src.originalPolicyNumber);
  }
  if ((src.versionsImported ?? 0) > 1) {
    known('Versions brought across', `${src.versionsImported}, the newest of them current here`);
  }
  known('Import reference', src.importJobId);
  // The hash of the spreadsheet as it was read, so the file a customer keeps
  // can be checked against the one StandingDocs imported.
  known('Spreadsheet checksum (SHA-256)', src.sourceFileSha256
    ? <code style={{ wordBreak: 'break-all' }}>{src.sourceFileSha256}</code>
    : null);

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Where this came from</h2>
      <p className={styles.itemMeta} style={{ maxWidth: '78ch' }}>
        This policy was brought into StandingDocs by a library import rather than written here.
        StandingDocs did not observe its approval or its original publication and claims neither;
        what follows is the migration itself.
      </p>
      {rows.length === 0 ? (
        <p className={styles.itemMeta}>
          It was imported, and nothing further about the import was recorded at the time.
        </p>
      ) : (
        <dl className={styles.facts}>
          {rows.map(([term, value]) => (
            <div key={term}>
              <dt className={styles.factTerm}>{term}</dt>
              <dd className={styles.factValue}>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
